import type {
  DynamicPriceRow,
  DynamicPricing,
  DynamicPricingWarning,
  DynamicPromotion,
  DynamicSeason,
  DynamicSupplement,
  DynamicSurcharge,
} from '../schemas/dynamic-pricing';
import type {
  PaxProductPriceRow,
  ProductRoomPrice,
  ProductRoomPricePeriod,
  SingleProductPrice,
} from '../schemas/products';
import { DYNAMIC_MIN_ROOM_SIZE_M2 } from '../schemas/dynamic-pricing';
import { computePricingRow } from './compute-row';
import { seasonLabel } from './dynamic-pricing-rules';
import { roundMoneyAmount } from './engine';

// The dynamic pricing FUNCTION (XT-951). Pure: a product's dynamic structure
// plus the booking parameters in, the same static price JSON an itinerary item
// carries today out — so the pricing table, engine and exports need no change.
// Rules: apps/products/prd/dynamic-pricing.md §"Pricing function".
//
// Output shape by pricing option:
//   Night → `{ rooms }`, one room picked by occupancy for the travellers per
//           room, with one price period per run of nights at the same rate;
//   Pax   → `{ travellers }`, one row per traveller type;
//   every other option → one flat row for the whole booking (`Unit`, 1 unit),
//           because a base-plus-additional group rate, a minimum quantity or a
//           per-booking surcharge has no exact per-unit form.
// Surcharges, mandatory supplements and promotions are folded into the money.

/**
 * Dynamic pricing is on. `false` switches it off without losing anything:
 * every itinerary item then prices from its product's static price, the
 * product editor hides the dynamic pricing form and the item Pricing tab its
 * status panel, while stored structures and item states are kept and a hand
 * price edit still turns an auto item manual. Off from 2026-10-28 to
 * 2026-11-04 (XT951-029/030).
 */
export const DYNAMIC_PRICING_ENABLED: boolean = true;

export type DynamicPricingTraveller = {
  travellerTypeId: string | null;
  /** Recorded age, when the itinerary names this traveller with one. */
  age?: number | null;
};

export type DynamicPricingParams = {
  /** Item start / end — UTC wall-clock ISO strings. */
  startTime: string;
  endTime: string | null;
  /** One entry per head (`travellerHeads`). */
  travellers: readonly DynamicPricingTraveller[];
  /** The day the quote is made (`YYYY-MM-DD`), for book-before promotions. */
  bookingDate: string;
  /** VAT rate id → percent, for the organisation's VAT settings. */
  vatPercentById: Readonly<Record<string, number>>;
  /** Accommodation Pax rates are per person per night. */
  contentCategory?: string | null;
  /** Hotel rooms booked on the item (its rooms' quantities); 1 when new. */
  roomCount?: number;
  /**
   * The product has no static price: gaps take the structure's
   * `fallbackAmount` instead of 0.
   */
  useFallback?: boolean;
  /** Traveller type id → its title, so a warning can name the type. */
  travellerTypeTitleById?: Readonly<Record<string, string>>;
  /**
   * The organisation's default markup for the product's category: a net row
   * with no markup of its own is priced at it. A gross row's margin is the
   * supplier commission, so it never applies there.
   */
  defaultMarkupPercent?: number | null;
};

export type DynamicPriceLine = {
  label: string;
  quantity: number;
  /** Pax lines: the traveller type the line prices. */
  travellerTypeId?: string;
  /** Cost incl. cost VAT, for the whole line (the gross, for gross rates). */
  cost: number;
  /** Paid to the supplier for the line, incl. VAT (gross less commission). */
  payable: number;
  /** Sell price incl. price VAT, for the whole line. */
  priceIncl: number;
};

export type DynamicPriceResult = {
  pricingType: 'Night' | 'Pax' | 'Unit';
  isGross: boolean;
  currency: string;
  costVatId: string | null;
  priceVatId: string | null;
  price:
    | SingleProductPrice
    | { travellers: PaxProductPriceRow[] }
    | { rooms: ProductRoomPriceOut[] };
  groupPriceThresholds: [];
  pricingUnits: 1;
  /** One line per room alternative / traveller type / the booking. */
  lines: DynamicPriceLine[];
  appliedPromotions: string[];
  warnings: DynamicPricingWarning[];
};

export type ProductRoomPriceOut = ProductRoomPrice & {
  roomTitle: string;
  pricePeriods: ProductRoomPricePeriod[];
};

// ---------------------------------------------------------------------------
// Dates — `YYYY-MM-DD` strings, UTC only.

const DAY_MS = 86_400_000;

const toDay = (iso: string): string => iso.slice(0, 10);

const dayMs = (day: string): number => {
  const [y, m, d] = day.split('-').map(Number);
  return Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1);
};

const addDays = (day: string, n: number): string =>
  new Date(dayMs(day) + n * DAY_MS).toISOString().slice(0, 10);

/** 0 = Monday … 6 = Sunday. */
const weekday = (day: string): number =>
  (new Date(dayMs(day)).getUTCDay() + 6) % 7;

const isoMs = (iso: string): number => {
  const normalised = /[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`;
  return new Date(normalised.replace(' ', 'T')).getTime();
};

const timeOfDay = (iso: string): string => {
  const match = /[T ](\d\d:\d\d)/.exec(iso);
  return match?.[1] ?? '00:00';
};

const inRange = (
  day: string,
  from: string | null | undefined,
  to: string | null | undefined
): boolean => (!from || day >= from) && (!to || day <= to);

// ---------------------------------------------------------------------------
// Money — integer cents so folding never drifts.

const cents = (amount: number): number => Math.round(amount * 100);
const fromCents = (value: number): number => value / 100;
// A share of an amount (a percentage off, a % surcharge, the payable after
// commission) to the cent, half a cent up: 30 % off 12.85 is 9.00, though
// 1285 × 0.7 is 899.4999… in floating point (XT951-040).
const roundCents = (value: number): number =>
  cents(roundMoneyAmount(fromCents(value)));

// ---------------------------------------------------------------------------

type Ctx = {
  config: DynamicPricing;
  params: DynamicPricingParams;
  warnings: DynamicPricingWarning[];
};

// One warning per code, season, traveller type and text: two seasons with the
// same name keep a warning each.
const warn = (
  ctx: Ctx,
  code: DynamicPricingWarning['code'],
  message: string,
  about: { seasonId?: string; travellerTypeId?: string } = {}
): void => {
  if (
    !ctx.warnings.some(
      (w) =>
        w.code === code &&
        w.message === message &&
        w.seasonId === about.seasonId &&
        w.travellerTypeId === about.travellerTypeId
    )
  ) {
    ctx.warnings.push({
      code,
      message,
      ...(about.seasonId ? { seasonId: about.seasonId } : {}),
      ...(about.travellerTypeId
        ? { travellerTypeId: about.travellerTypeId }
        : {}),
    });
  }
};

/** The most specific (shortest) season containing the day. */
const seasonFor = (
  seasons: readonly DynamicSeason[],
  day: string
): DynamicSeason | null => {
  const matching = seasons.filter((s) => day >= s.from && day <= s.to);
  if (matching.length === 0) return null;
  return matching.reduce((best, s) =>
    dayMs(s.to) - dayMs(s.from) < dayMs(best.to) - dayMs(best.from) ? s : best
  );
};

/** The fallback amount in cents, when this booking may use it. */
const fallbackCents = (ctx: Ctx): number | null =>
  ctx.params.useFallback && ctx.config.fallbackAmount != null
    ? cents(ctx.config.fallbackAmount)
    : null;

const FALLBACK_LABEL = 'Fallback price';

const warnFallback = (ctx: Ctx, what: string): void =>
  warn(
    ctx,
    'FALLBACK_PRICE',
    `No contract rate for ${what}: priced at the product's fallback price.`
  );

const rowsOf = (config: DynamicPricing, seasonId: string): DynamicPriceRow[] =>
  config.rows.filter((row) => row.seasonId === seasonId);

const headCount = (ctx: Ctx): number => ctx.params.travellers.length;

const countByType = (ctx: Ctx): Map<string | null, number> => {
  const counts = new Map<string | null, number>();
  for (const t of ctx.params.travellers) {
    counts.set(t.travellerTypeId, (counts.get(t.travellerTypeId) ?? 0) + 1);
  }
  return counts;
};

/** Nights / days / hours the item spans, each at least 1. */
const spans = (params: DynamicPricingParams) => {
  const startDay = toDay(params.startTime);
  const endDay = params.endTime ? toDay(params.endTime) : startDay;
  const nights = Math.max(
    1,
    Math.round((dayMs(endDay) - dayMs(startDay)) / DAY_MS)
  );
  const ms = params.endTime
    ? Math.max(0, isoMs(params.endTime) - isoMs(params.startTime))
    : 0;
  return {
    startDay,
    nights,
    days: Math.max(1, Math.ceil(ms / DAY_MS)),
    hours: Math.max(1, Math.ceil(ms / 3_600_000)),
  };
};

const surchargeAppliesOn = (s: DynamicSurcharge, day: string): boolean =>
  inRange(day, s.from, s.to) &&
  (s.weekdays.length === 0 || s.weekdays.includes(weekday(day)));

const NIGHT_SUPPLEMENT_TO = '06:00';

/**
 * Whether the item starts inside the night window: from
 * `nightSupplementFrom` to `nightSupplementTo` (06:00 when blank), past
 * midnight when it ends earlier than it starts. The start time is judged.
 */
const nightSupplementApplies = (ctx: Ctx): boolean => {
  const from = ctx.config.operation?.nightSupplementFrom;
  if (!from) return false;
  const to = ctx.config.operation?.nightSupplementTo || NIGHT_SUPPLEMENT_TO;
  const time = timeOfDay(ctx.params.startTime);
  return from < to ? time >= from && time < to : time >= from || time < to;
};

/**
 * Surcharges that touch the service dates. `Overtime` needs hours beyond the
 * contract that the item does not record, so it is never auto-applied.
 */
const activeSurcharges = (
  ctx: Ctx,
  serviceDays: readonly string[]
): DynamicSurcharge[] =>
  ctx.config.surcharges.filter((s) => {
    if (s.type === 'Overtime') return false;
    if (s.type === 'Night supplement' && !nightSupplementApplies(ctx)) {
      return false;
    }
    return serviceDays.some((day) => surchargeAppliesOn(s, day));
  });

const flagAvailability = (ctx: Ctx, surcharges: DynamicSurcharge[]): void => {
  for (const s of surcharges) {
    if (s.availability === 'stop_sell') {
      warn(ctx, 'STOP_SELL', `${s.name || s.type}: stop sell on these dates.`);
    } else if (s.availability === 'on_request') {
      warn(ctx, 'ON_REQUEST', `${s.name || s.type}: on request.`);
    }
  }
};

/** Per-booking extras (cents): booking / pax / hour / vehicle surcharges. */
const bookingSurchargeCents = (
  ctx: Ctx,
  surcharges: DynamicSurcharge[],
  baseCents: number,
  hours: number
): number =>
  surcharges.reduce((total, s) => {
    if (s.basis === 'per_room_night') return total;
    if (s.unit === 'percent') {
      return total + roundCents((baseCents * s.amount) / 100);
    }
    const multiplier =
      s.basis === 'per_pax'
        ? headCount(ctx)
        : s.basis === 'per_hour'
          ? hours
          : 1;
    return total + cents(s.amount) * multiplier;
  }, 0);

const supplementQuantity = (
  ctx: Ctx,
  s: DynamicSupplement,
  span: { nights: number; days: number; hours: number }
): number => {
  switch (s.pricingOption) {
    case 'Pax': {
      const heads = s.travellerTypeId
        ? (countByType(ctx).get(s.travellerTypeId) ?? 0)
        : headCount(ctx);
      return heads === 0 ? 0 : Math.max(heads, s.minQuantity);
    }
    case 'Night':
      return Math.max(span.nights, s.minQuantity);
    case 'Day':
      return Math.max(span.days, s.minQuantity);
    case 'Hour':
      return Math.max(span.hours, s.minQuantity);
    default:
      return s.minQuantity;
  }
};

/** Mandatory supplements, folded into the price (cents). */
const mandatorySupplementCents = (
  ctx: Ctx,
  span: { nights: number; days: number; hours: number }
): number =>
  ctx.config.supplements.reduce((total, s) => {
    if (!s.mandatory) return total;
    if (s.amount == null) {
      warn(
        ctx,
        'SUPPLEMENT_ON_REQUEST',
        `${s.name || 'Supplement'}: mandatory, price on request.`
      );
      return total;
    }
    return total + cents(s.amount) * supplementQuantity(ctx, s, span);
  }, 0);

const eligiblePromotions = (
  ctx: Ctx,
  span: { startDay: string; nights: number },
  roomTypeId: string | null
): DynamicPromotion[] => {
  const picked: DynamicPromotion[] = [];
  for (const p of ctx.config.promotions) {
    if (p.bookBefore && ctx.params.bookingDate > p.bookBefore) continue;
    if (!inRange(span.startDay, p.stayFrom, p.stayTo)) continue;
    if (p.minNights && span.nights < p.minNights) continue;
    if (p.excludedArrivalWeekdays.includes(weekday(span.startDay))) continue;
    if (
      p.roomTypeIds.length > 0 &&
      (!roomTypeId || !p.roomTypeIds.includes(roomTypeId))
    ) {
      continue;
    }
    picked.push(p);
  }
  return picked;
};

/**
 * The promotions a booking may take together: every combinable one, or one
 * non-combinable one alone.
 */
const promotionSets = (eligible: DynamicPromotion[]): DynamicPromotion[][] => {
  const combinable = eligible.filter((p) => p.combinable);
  const sets = [
    ...(combinable.length > 0 ? [combinable] : []),
    ...eligible.filter((p) => !p.combinable).map((p) => [p]),
  ];
  return sets.length > 0 ? sets : [[]];
};

/**
 * Prices the booking under each set of promotions it may take and keeps the
 * one that leaves the lowest total — the first on a tie — whatever order the
 * promotions are listed in.
 */
const cheapest = <T extends { total: number }>(
  eligible: DynamicPromotion[],
  price: (promotions: DynamicPromotion[]) => T
): T & { promotions: DynamicPromotion[] } => {
  const [first, ...rest] = promotionSets(eligible);
  const firstSet = first ?? [];
  let best = { ...price(firstSet), promotions: firstSet };
  for (const promotions of rest) {
    const result = price(promotions);
    if (result.total < best.total) best = { ...result, promotions };
  }
  return best;
};

/**
 * The applied promotions a booking lists: a % discount only when there was
 * money to take it from, a perk (upgrade, breakfast, credit, gift) whenever it
 * applies. A stay-X-pay-Y frees nights only on Night pricing, which lists it
 * itself when it freed one.
 */
const listedPromotions = (
  promotions: DynamicPromotion[],
  baseCents: number
): string[] =>
  promotions
    .filter((p) =>
      p.type === 'stay_pay'
        ? false
        : p.type === 'percent_discount'
          ? Boolean(p.percent) && baseCents > 0
          : true
    )
    .map(promotionLabel);

const promotionLabel = (p: DynamicPromotion): string => {
  const ref = p.reference ? ` (${p.reference})` : '';
  switch (p.type) {
    case 'stay_pay':
      return `Stay ${p.stayNights ?? '?'} pay ${p.payNights ?? '?'}${ref}`;
    case 'percent_discount':
      return `${p.percent ?? 0}% discount${ref}`;
    default:
      return `${p.description || p.type.replace(/_/g, ' ')}${ref}`;
  }
};

const applyPercentDiscounts = (
  promotions: DynamicPromotion[],
  value: number
): number =>
  promotions.reduce(
    (current, p) =>
      p.type === 'percent_discount' && p.percent
        ? roundCents(current * (1 - p.percent / 100))
        : current,
    value
  );

// ---------------------------------------------------------------------------
// Cost → static row money, through the shared engine (`computePricingRow`).

type RowMoney = {
  /** Paid to the supplier, incl. cost VAT: gross less the commission. */
  payable: number;
  cost: number;
  netCost: number;
  price: number;
  markup: number;
  commission: number;
  priceIncl: number;
};

const vatPercent = (
  ctx: Ctx,
  vatId: string | null | undefined
): number | null => (vatId ? (ctx.params.vatPercentById[vatId] ?? null) : null);

const toRowMoney = (
  ctx: Ctx,
  costCents: number,
  vat: {
    costVatId: string | null;
    priceVatId: string | null;
    markup: number | null;
  }
): RowMoney => {
  const amount = fromCents(costCents);
  const isGross = ctx.config.priceType === 'gross';
  // A row with no markup of its own takes the organisation's default markup
  // for the category (XT951-043). A markup at or below −100 % sells at
  // nothing or less: the editor and the server refuse one, but a structure
  // stored before is never re-checked, so it prices at 0 and says so.
  const own = vat.markup ?? ctx.params.defaultMarkupPercent ?? 0;
  const markup = Math.max(own, -100);
  if (!isGross && own < -100) {
    warn(
      ctx,
      'NEGATIVE_PRICE',
      `A rate's markup (${own} %) gives a negative price: priced at 0. Fix the markup before quoting.`
    );
  }
  const computed = computePricingRow(
    isGross
      ? {
          pricingType: 'gross',
          costVatPercent: vatPercent(ctx, vat.costVatId),
          priceVatPercent: vatPercent(ctx, vat.priceVatId),
          // Gross: the supplier gross is the sell price before price VAT and
          // the margin is the supplier commission — the engine's own gross
          // model (profit = price excl. VAT − cost + commission), so profit
          // reads as the commission. Decision 2026-10-19.
          lastEdited: 'markup',
          row: {
            cost: amount,
            markup: 0,
            commission: ctx.config.commissionPercent ?? 0,
          },
        }
      : {
          pricingType: 'net',
          costVatPercent: vatPercent(ctx, vat.costVatId),
          priceVatPercent: vatPercent(ctx, vat.priceVatId),
          lastEdited: 'markup',
          row: { cost: amount, markup },
        }
  );
  return {
    payable: isGross
      ? fromCents(
          roundCents(
            costCents * (1 - (ctx.config.commissionPercent ?? 0) / 100)
          )
        )
      : amount,
    cost: computed.cost ?? amount,
    netCost: computed.netCost ?? amount,
    price: computed.price ?? 0,
    markup: computed.markup ?? 0,
    commission: computed.commission ?? 0,
    priceIncl: computed.priceIncl ?? 0,
  };
};

const rowVat = (row: DynamicPriceRow | undefined) => ({
  costVatId: row?.costVatId ?? null,
  priceVatId: row?.priceVatId ?? null,
  markup: row?.markup ?? null,
});

const moneyFields = (money: RowMoney) => ({
  cost: money.cost,
  netCost: money.netCost,
  price: money.price,
  markup: money.markup,
  commission: money.commission,
});

// ---------------------------------------------------------------------------
// General checks shared by every option.

const checkContractAndSeasons = (ctx: Ctx, serviceDays: string[]): void => {
  const { contractFrom, contractTo, rateStatus } = ctx.config;
  if (serviceDays.some((day) => !inRange(day, contractFrom, contractTo))) {
    warn(
      ctx,
      'OUTSIDE_CONTRACT',
      `Service dates fall outside the contract (${contractFrom ?? '…'} – ${contractTo ?? '…'}).`
    );
  }
  if (rateStatus === 'on_request') {
    warn(ctx, 'ON_REQUEST', 'Rate is on request: confirm with the supplier.');
  }
  const { groupThreshold, pricingOption } = ctx.config;
  // Pax and Group price by the head: a booking with nobody on it is a gap to
  // check, not a price.
  if (
    headCount(ctx) === 0 &&
    (pricingOption === 'Pax' || pricingOption === 'Group')
  ) {
    warn(
      ctx,
      'NO_TRAVELLERS',
      'No travellers on this booking: check the party before quoting.'
    );
  }
  if (groupThreshold && pricingOption !== 'Night') {
    if (headCount(ctx) >= groupThreshold) {
      warn(
        ctx,
        'GROUP_THRESHOLD',
        `${headCount(ctx)} travellers reach the group threshold (${groupThreshold}): group terms apply.`
      );
    }
  }
};

// ---------------------------------------------------------------------------
// Night — rooms with nightly price periods.

type RoomKey = {
  key: string;
  roomTypeId: string | null;
  occupancy: string | null;
  board: string | null;
};

const roomKeyOf = (row: DynamicPriceRow): RoomKey => ({
  key: [row.roomTypeId ?? '', row.occupancy ?? '', row.board ?? ''].join('|'),
  roomTypeId: row.roomTypeId ?? null,
  occupancy: row.occupancy ?? null,
  board: row.board ?? null,
});

/** Guests an occupancy sleeps (`Single` 1, `Double` / `Twin` 2, …), or null. */
const occupancyCapacity = (occupancy: string | null): number | null => {
  const text = occupancy?.trim().toLowerCase() ?? '';
  if (!text) return null;
  const digits = /\d+/.exec(text);
  if (digits) return Number(digits[0]);
  if (/single|sgl/.test(text)) return 1;
  if (/double|twin|dbl|twn/.test(text)) return 2;
  if (/triple|tpl/.test(text)) return 3;
  if (/quad/.test(text)) return 4;
  return null;
};

/**
 * One room for the booking: the first whose occupancy sleeps exactly the
 * travellers per room, else the smallest that sleeps them, else the largest;
 * rooms without an occupancy only when none has one that fits.
 */
const pickRoomByOccupancy = (
  keys: readonly RoomKey[],
  perRoom: number
): RoomKey | undefined => {
  const sized = keys
    .map((key) => ({ key, capacity: occupancyCapacity(key.occupancy) }))
    .filter(
      (entry): entry is { key: RoomKey; capacity: number } =>
        entry.capacity !== null
    );
  const exact = sized.find((entry) => entry.capacity === perRoom);
  if (exact) return exact.key;
  const fits = sized
    .filter((entry) => entry.capacity > perRoom)
    .sort((a, b) => a.capacity - b.capacity)[0];
  if (fits) return fits.key;
  const unsized = keys.find((key) => occupancyCapacity(key.occupancy) === null);
  if (unsized) return unsized;
  return [...sized].sort((a, b) => b.capacity - a.capacity)[0]?.key;
};

/** The row for a room on one night: the longest min-stay the stay meets. */
const nightRow = (
  rows: DynamicPriceRow[],
  key: string,
  nights: number
): DynamicPriceRow | undefined =>
  rows
    .filter(
      (row) => roomKeyOf(row).key === key && (row.minNights ?? 1) <= nights
    )
    .sort((a, b) => (b.minNights ?? 1) - (a.minNights ?? 1))[0];

const priceNights = (ctx: Ctx): DynamicPriceResult => {
  const { config, params } = ctx;
  const span = spans(params);
  const nightDays = Array.from({ length: span.nights }, (_, i) =>
    addDays(span.startDay, i)
  );
  checkContractAndSeasons(ctx, nightDays);

  const seasonByDay = nightDays.map((day) => seasonFor(config.seasons, day));
  nightDays.forEach((day, i) => {
    if (!seasonByDay[i]) warn(ctx, 'NO_SEASON', `No season covers ${day}.`);
  });
  const roomCount = Math.max(1, params.roomCount ?? 1);
  if (config.groupThreshold && roomCount >= config.groupThreshold) {
    warn(
      ctx,
      'GROUP_THRESHOLD',
      groupThresholdMessage(roomCount, config.groupThreshold)
    );
  }
  const arrivalSeason = seasonByDay[0];
  if (arrivalSeason?.minStay && span.nights < arrivalSeason.minStay) {
    warn(
      ctx,
      'MIN_STAY',
      `Season ${seasonLabel(config.seasons, arrivalSeason)} requires at least ${arrivalSeason.minStay} nights.`,
      { seasonId: arrivalSeason.id }
    );
  }

  // The room priced: one room key, picked by occupancy for the travellers
  // per room booked, among the rooms priced in a season the stay touches —
  // or, when it touches none, among every room the product prices.
  const keys = new Map<string, RoomKey>();
  const addKeys = (rows: DynamicPriceRow[]) => {
    for (const row of rows) {
      const k = roomKeyOf(row);
      if (!keys.has(k.key)) keys.set(k.key, k);
    }
  };
  for (const season of seasonByDay) {
    if (season) addKeys(rowsOf(config, season.id));
  }
  if (keys.size === 0) addKeys(config.rows);
  // Among them, the rooms with a rate in every season the stay touches, when
  // there are any: a room missing one would price those nights at nothing.
  const touched = seasonByDay.filter(
    (season): season is DynamicSeason => season !== null
  );
  const candidates = [...keys.values()];
  const complete = candidates.filter((k) =>
    touched.every((season) =>
      nightRow(rowsOf(config, season.id), k.key, span.nights)
    )
  );
  const picked = pickRoomByOccupancy(
    complete.length > 0 ? complete : candidates,
    Math.ceil(headCount(ctx) / roomCount)
  );
  keys.clear();
  if (picked) keys.set(picked.key, picked);

  const surcharges = activeSurcharges(ctx, nightDays);
  flagAvailability(ctx, surcharges);
  const roomNightSurcharges = surcharges.filter(
    (s) => s.basis === 'per_room_night'
  );
  const supplementCents = mandatorySupplementCents(ctx, span);
  const appliedPromotions = new Set<string>();
  const roomTypeName = (id: string | null) =>
    config.roomTypes.find((r) => r.id === id)?.name ?? null;

  for (const room of config.roomTypes) {
    if (room.sizeMin != null && room.sizeMin < DYNAMIC_MIN_ROOM_SIZE_M2) {
      warn(
        ctx,
        'ROOM_UNDER_MIN_SIZE',
        `${room.name}: under ${DYNAMIC_MIN_ROOM_SIZE_M2} m². Inform the agent before quoting.`
      );
    }
  }

  const rooms: ProductRoomPriceOut[] = [];
  const lines: DynamicPriceLine[] = [];
  let firstRow: DynamicPriceRow | undefined;

  for (const k of keys.values()) {
    const roomType = config.roomTypes.find((r) => r.id === k.roomTypeId);
    if (roomType?.maxPax && headCount(ctx) > roomType.maxPax) {
      warn(
        ctx,
        'ABOVE_MAX_PAX',
        `${roomType.name} sleeps ${roomType.maxPax}; ${headCount(ctx)} travellers booked.`
      );
    }
    const label =
      [roomTypeName(k.roomTypeId), k.occupancy, k.board]
        .filter(Boolean)
        .join(' · ') || 'Room';

    // Nightly cost (cents) for this room, surcharges included, and the row
    // each night is priced from (its markup and VAT).
    let roomRow: DynamicPriceRow | undefined;
    const nightRows: (DynamicPriceRow | undefined)[] = [];
    const nightly = nightDays.map((day, i) => {
      const season = seasonByDay[i];
      const row = season
        ? nightRow(rowsOf(config, season.id), k.key, span.nights)
        : undefined;
      nightRows.push(row);
      if (!row && season) {
        warn(ctx, 'NO_RATE', `${label}: no rate for ${day}.`);
      }
      const base = row ? cents(row.amount) : fallbackCents(ctx);
      if (base === null) return 0;
      if (row) roomRow ??= row;
      else warnFallback(ctx, day);
      const extra = roomNightSurcharges.reduce(
        (sum, s) =>
          surchargeAppliesOn(s, day)
            ? sum +
              (s.unit === 'percent'
                ? roundCents((base * s.amount) / 100)
                : cents(s.amount))
            : sum,
        0
      );
      return base + extra;
    });
    // A room priced only from the fallback takes the first row's VAT/markup.
    roomRow ??= nightly.some((value) => value > 0) ? config.rows[0] : undefined;
    firstRow ??= roomRow;

    // Promotions for this room: the set that leaves the stay cheapest.
    const priceStay = (promotions: DynamicPromotion[]) => {
      let adjusted = nightly.map((value) =>
        applyPercentDiscounts(promotions, value)
      );
      const listed = listedPromotions(
        promotions,
        nightly.reduce((sum, value) => sum + value, 0)
      );
      for (const p of promotions) {
        if (p.type !== 'stay_pay' || !p.stayNights || p.payNights == null) {
          continue;
        }
        const free =
          Math.floor(span.nights / p.stayNights) *
          Math.max(0, p.stayNights - p.payNights);
        // The cheapest nights go free.
        const freed = adjusted
          .map((value, index) => ({ value, index }))
          .sort((a, b) => a.value - b.value || b.index - a.index)
          .slice(0, free);
        // Listed only when it freed a night that cost something.
        if (!freed.some((entry) => entry.value > 0)) continue;
        const freeIndexes = new Set(freed.map((entry) => entry.index));
        adjusted = adjusted.map((value, index) =>
          freeIndexes.has(index) ? 0 : value
        );
        listed.push(promotionLabel(p));
      }
      return {
        adjusted,
        listed,
        total: adjusted.reduce((sum, value) => sum + value, 0),
      };
    };
    const { adjusted, listed } = cheapest(
      eligiblePromotions(ctx, span, k.roomTypeId),
      priceStay
    );
    for (const label of listed) appliedPromotions.add(label);

    // Booking-level extras land on the first night. The room is booked
    // `roomCount` times (its units), so it carries its share of them.
    const subtotal = adjusted.reduce((sum, value) => sum + value, 0);
    const bookingExtra = Math.round(
      (bookingSurchargeCents(
        ctx,
        surcharges,
        subtotal * roomCount,
        span.hours
      ) +
        supplementCents) /
        roomCount
    );
    if (adjusted.length > 0) adjusted[0] = (adjusted[0] ?? 0) + bookingExtra;

    // Every night takes its own row's markup (a fallback night, its room's).
    // The item carries one VAT, its arrival night's (the first night priced
    // from a row); a night whose season charges another rate is priced at it
    // too, and flagged.
    const vat = rowVat(roomRow);
    const vatOf = (row: DynamicPriceRow) =>
      [vatPercent(ctx, row.costVatId), vatPercent(ctx, row.priceVatId)].join();
    const arrivalVat = roomRow ? vatOf(roomRow) : null;
    const otherVat = nightRows.filter(
      (row): row is DynamicPriceRow =>
        row !== undefined && arrivalVat !== null && vatOf(row) !== arrivalVat
    );
    if (otherVat.length > 0) {
      const seasons = [
        ...new Set(
          otherVat.map((row) => {
            const season = config.seasons.find((s) => s.id === row.seasonId);
            return season
              ? `Season ${seasonLabel(config.seasons, season)}`
              : 'another season';
          })
        ),
      ];
      warn(
        ctx,
        'MIXED_VAT',
        `${label}: ${seasons.join(', ')} charges a different VAT; the whole stay is priced at the arrival night's VAT. Check the VAT before quoting.`
      );
    }
    // One period per run of nights at the same rate and markup.
    const periods: ProductRoomPricePeriod[] = [];
    let costTotal = 0;
    let payableTotal = 0;
    let priceInclTotal = 0;
    adjusted.forEach((value, index) => {
      const last = periods.at(-1);
      const night = nightRows[index];
      const money = toRowMoney(ctx, value, {
        ...vat,
        markup: night ? rowVat(night).markup : vat.markup,
      });
      costTotal += cents(money.cost);
      payableTotal += cents(money.payable);
      priceInclTotal += cents(money.priceIncl);
      if (
        last &&
        cents(last.cost) === cents(money.cost) &&
        last.markup === money.markup &&
        last.startNight + last.nightCount === index + 1
      ) {
        last.nightCount += 1;
        return;
      }
      periods.push({
        startNight: index + 1,
        nightCount: 1,
        ...moneyFields(money),
      });
    });
    // The room's own cost / net cost / price are the stay totals (each
    // night's amount × its nights) — the convention the planner's per-night
    // refit (`fitAccommodationPriceToDates`) enforces on every write.
    const stayTotal = (field: 'cost' | 'netCost' | 'price') =>
      fromCents(
        periods.reduce(
          (sum, period) => sum + cents(period[field] ?? 0) * period.nightCount,
          0
        )
      );
    const first = periods[0];
    rooms.push({
      roomType: label,
      roomTitle: label,
      cost: stayTotal('cost'),
      netCost: stayTotal('netCost'),
      price: stayTotal('price'),
      markup: first?.markup ?? 0,
      commission: first?.commission ?? 0,
      // The rooms booked: the room's money is one room's stay.
      units: roomCount,
      pricePeriods: periods,
    });
    lines.push({
      label: roomCount > 1 ? `${roomCount} × ${label}` : label,
      quantity: span.nights,
      cost: fromCents(costTotal * roomCount),
      payable: fromCents(payableTotal * roomCount),
      priceIncl: fromCents(priceInclTotal * roomCount),
    });
  }

  if (rooms.length === 0) {
    warn(ctx, 'NO_RATE', 'No room rate covers these dates.');
  }

  const vat = rowVat(firstRow);
  return {
    pricingType: 'Night',
    isGross: config.priceType === 'gross',
    currency: config.currency,
    costVatId: vat.costVatId,
    priceVatId: vat.priceVatId,
    price: { rooms },
    groupPriceThresholds: [],
    pricingUnits: 1,
    lines,
    appliedPromotions: [...appliedPromotions],
    warnings: ctx.warnings,
  };
};

// ---------------------------------------------------------------------------
// Pax — one row per traveller type.

const pricePax = (ctx: Ctx): DynamicPriceResult => {
  const { config, params } = ctx;
  const span = spans(params);
  const serviceDays = [span.startDay];
  checkContractAndSeasons(ctx, serviceDays);
  const season = seasonFor(config.seasons, span.startDay);
  if (!season) warn(ctx, 'NO_SEASON', `No season covers ${span.startDay}.`);
  const rows = season ? rowsOf(config, season.id) : [];
  const perNight = params.contentCategory === 'Accommodation';
  const multiplier = perNight ? span.nights : 1;

  const surcharges = activeSurcharges(ctx, serviceDays);
  flagAvailability(ctx, surcharges);

  const counts = countByType(ctx);
  const inRange = (row: DynamicPriceRow, age: number) =>
    (row.ageFrom != null || row.ageTo != null) &&
    (row.ageFrom == null || age >= row.ageFrom) &&
    (row.ageTo == null || age <= row.ageTo);
  // A traveller with a recorded age takes the row whose age range holds it —
  // its own type's first, else any — and otherwise the row of its type.
  const rowFor = (
    head: DynamicPricingTraveller
  ): DynamicPriceRow | undefined => {
    if (head.age != null) {
      const age = head.age;
      const byAge =
        rows.find(
          (r) => r.travellerTypeId === head.travellerTypeId && inRange(r, age)
        ) ?? rows.find((r) => inRange(r, age));
      if (byAge) return byAge;
    }
    return rows.find((r) => r.travellerTypeId === head.travellerTypeId);
  };
  // Per-person cost (cents) for each traveller type that travels: one price
  // per type, so travellers of one type at different ages are averaged.
  // A traveller priced from the fallback has no row of its own.
  type PaxEntry = { row: DynamicPriceRow | undefined; label: string };
  const perHead = new Map<string, PaxEntry & { value: number }>();
  const fallback = fallbackCents(ctx);
  const priceHeads = (promotions: DynamicPromotion[]) => {
    const sums = new Map<string, PaxEntry & { total: number; heads: number }>();
    let base = 0;
    let total = 0;
    for (const head of params.travellers) {
      const typeId = head.travellerTypeId;
      const row = typeId ? rowFor(head) : undefined;
      if (!typeId) {
        warn(
          ctx,
          'NO_TRAVELLER_RATE',
          'A traveller has no traveller type: priced at 0.'
        );
        continue;
      }
      if (!row && fallback === null) {
        const title = params.travellerTypeTitleById?.[typeId];
        warn(
          ctx,
          'NO_TRAVELLER_RATE',
          title
            ? `"${title}" has no Pax rate in this season: priced at 0.`
            : 'A traveller type has no Pax rate in this season: priced at 0.',
          { travellerTypeId: typeId }
        );
        continue;
      }
      if (!row) warnFallback(ctx, 'a traveller');
      const undiscounted =
        (row ? cents(row.amount) : (fallback ?? 0)) * multiplier;
      const value = applyPercentDiscounts(promotions, undiscounted);
      const entry = sums.get(typeId) ?? {
        row: row ?? config.rows[0],
        label: row ? row.description || 'Traveller' : FALLBACK_LABEL,
        total: 0,
        heads: 0,
      };
      entry.total += value;
      entry.heads += 1;
      sums.set(typeId, entry);
      base += undiscounted;
      total += value;
    }
    return { sums, base, total };
  };
  const { sums, base, promotions } = cheapest(
    eligiblePromotions(ctx, span, null),
    priceHeads
  );
  const appliedPromotions = listedPromotions(promotions, base);
  for (const [typeId, entry] of sums) {
    const value = Math.round(entry.total / entry.heads);
    if (value * entry.heads !== entry.total) {
      warn(
        ctx,
        'AGE_RATES_AVERAGED',
        `Travellers of one type pay different rates by age; their price was averaged (differs by ${fromCents(
          Math.abs(value * entry.heads - entry.total)
        ).toFixed(2)}).`
      );
    }
    perHead.set(typeId, { row: entry.row, label: entry.label, value });
  }
  // Heads counted per type for the lines and the booking spread.
  for (const [typeId] of perHead)
    counts.set(typeId, sums.get(typeId)?.heads ?? 0);

  const paidHeads = [...perHead.keys()].reduce(
    (sum, typeId) => sum + (counts.get(typeId) ?? 0),
    0
  );
  const subtotal = [...perHead].reduce(
    (sum, [typeId, entry]) => sum + entry.value * (counts.get(typeId) ?? 0),
    0
  );
  // Booking-level money is spread across the paying travellers.
  const bookingExtra =
    bookingSurchargeCents(ctx, surcharges, subtotal, span.hours) +
    mandatorySupplementCents(ctx, span);
  const extraPerHead = paidHeads > 0 ? Math.round(bookingExtra / paidHeads) : 0;
  if (paidHeads > 0 && extraPerHead * paidHeads !== bookingExtra) {
    warn(
      ctx,
      'SPREAD_PER_BOOKING',
      `Per-booking amounts were spread across ${paidHeads} travellers; the total differs by ${fromCents(
        Math.abs(extraPerHead * paidHeads - bookingExtra)
      ).toFixed(2)}.`
    );
  }

  // Nobody pays per head: the booking-level money (a booking fee, a
  // per-booking surcharge) is still owed, as one booking row — a Pax price
  // is read from its traveller rows only.
  if (perHead.size === 0 && bookingExtra > 0) {
    const vat = rowVat(rows[0]);
    const money = toRowMoney(ctx, bookingExtra, vat);
    return {
      pricingType: 'Unit',
      isGross: config.priceType === 'gross',
      currency: config.currency,
      costVatId: vat.costVatId,
      priceVatId: vat.priceVatId,
      price: { ...moneyFields(money), units: 1 },
      groupPriceThresholds: [],
      pricingUnits: 1,
      lines: [
        {
          label: 'Per booking',
          quantity: 1,
          cost: money.cost,
          payable: money.payable,
          priceIncl: money.priceIncl,
        },
      ],
      appliedPromotions,
      warnings: ctx.warnings,
    };
  }

  const travellers: PaxProductPriceRow[] = [];
  const lines: DynamicPriceLine[] = [];
  for (const [typeId, entry] of perHead) {
    const quantity = counts.get(typeId) ?? 0;
    const money = toRowMoney(
      ctx,
      entry.value + extraPerHead,
      rowVat(entry.row)
    );
    travellers.push({ travellerId: typeId, ...moneyFields(money) });
    lines.push({
      label: entry.label,
      quantity,
      travellerTypeId: typeId,
      cost: fromCents(cents(money.cost) * quantity),
      payable: fromCents(cents(money.payable) * quantity),
      priceIncl: fromCents(cents(money.priceIncl) * quantity),
    });
  }

  const vat = rowVat([...perHead.values()][0]?.row ?? rows[0]);
  return {
    pricingType: 'Pax',
    isGross: config.priceType === 'gross',
    currency: config.currency,
    costVatId: vat.costVatId,
    priceVatId: vat.priceVatId,
    price:
      travellers.length > 0
        ? { travellers }
        : { cost: 0, netCost: 0, price: 0, markup: 0, commission: 0 },
    groupPriceThresholds: [],
    pricingUnits: 1,
    lines,
    appliedPromotions,
    warnings: ctx.warnings,
  };
};

// ---------------------------------------------------------------------------
// Group / Hour / Day / Unit / Piece / Vehicle — one total for the booking.

/**
 * The best fit among the rows that fit the party: the smallest capacity (a
 * row with none ranks last), then the cheapest — whatever order they are
 * listed in.
 */
const bestFit = (
  rows: DynamicPriceRow[],
  capacity: (row: DynamicPriceRow) => number | null | undefined
): DynamicPriceRow | undefined =>
  [...rows].sort(
    (a, b) =>
      (capacity(a) ?? Number.POSITIVE_INFINITY) -
        (capacity(b) ?? Number.POSITIVE_INFINITY) || a.amount - b.amount
  )[0];

const pickRow = (
  ctx: Ctx,
  rows: DynamicPriceRow[]
): DynamicPriceRow | undefined => {
  const heads = headCount(ctx);
  if (ctx.config.pricingOption === 'Group') {
    const fits = bestFit(
      rows.filter(
        (row) =>
          (row.paxMin == null || heads >= row.paxMin) &&
          (row.paxMax == null || heads <= row.paxMax)
      ),
      (row) => row.paxMax
    );
    if (fits) return fits;
    const top = [...rows].sort((a, b) => (b.paxMax ?? 0) - (a.paxMax ?? 0))[0];
    if (top?.paxMax != null && heads > top.paxMax) {
      warn(
        ctx,
        'ABOVE_MAX_PAX',
        `${heads} travellers is above the max pax (${top.paxMax}). Quote on request.`
      );
    }
    return top;
  }
  const fits = bestFit(
    rows.filter((row) => row.maxPax == null || heads <= row.maxPax),
    (row) => row.maxPax
  );
  if (!fits && rows.length > 0) {
    warn(
      ctx,
      'ABOVE_MAX_PAX',
      `${heads} travellers is above every vehicle's max pax.`
    );
  }
  return fits ?? rows[0];
};

const priceBooking = (ctx: Ctx): DynamicPriceResult => {
  const { config, params } = ctx;
  const span = spans(params);
  const serviceDays = [span.startDay];
  checkContractAndSeasons(ctx, serviceDays);
  const season = seasonFor(config.seasons, span.startDay);
  if (!season) warn(ctx, 'NO_SEASON', `No season covers ${span.startDay}.`);
  const row = season ? pickRow(ctx, rowsOf(config, season.id)) : undefined;
  if (season && !row) warn(ctx, 'NO_RATE', 'No price row in this season.');

  const heads = headCount(ctx);
  let quantity = 1;
  let base = 0;
  let label =
    row?.description ||
    row?.vehicle ||
    row?.service ||
    row?.meal ||
    config.pricingOption;
  const fallback = row ? null : fallbackCents(ctx);
  if (fallback !== null) {
    base = fallback;
    label = FALLBACK_LABEL;
    warnFallback(ctx, 'these dates');
  }
  if (row) {
    switch (config.pricingOption) {
      case 'Group': {
        const basePax = row.basePax ?? 1;
        const extra = Math.max(0, heads - basePax);
        base = cents(row.amount) + extra * cents(row.additionalPaxAmount ?? 0);
        quantity = Math.max(heads, 1);
        label = `Base ${basePax} pax + ${extra} additional`;
        break;
      }
      case 'Hour':
        quantity = Math.max(span.hours, row.minQuantity ?? 1);
        base = cents(row.amount) * quantity;
        break;
      case 'Day':
        quantity = Math.max(span.days, row.minQuantity ?? 1);
        base = cents(row.amount) * quantity;
        break;
      default:
        quantity = row.minQuantity ?? 1;
        base = cents(row.amount) * quantity;
    }
    if (row.minSpend != null) base = Math.max(base, cents(row.minSpend));
  }

  const surcharges = activeSurcharges(ctx, serviceDays);
  flagAvailability(ctx, surcharges);
  const { total: discounted, promotions } = cheapest(
    eligiblePromotions(ctx, span, null),
    (set) => ({ total: applyPercentDiscounts(set, base) })
  );
  const total =
    discounted +
    bookingSurchargeCents(ctx, surcharges, discounted, span.hours) +
    mandatorySupplementCents(ctx, span);

  const vat = rowVat(row ?? (fallback !== null ? config.rows[0] : undefined));
  const money = toRowMoney(ctx, total, vat);
  return {
    pricingType: 'Unit',
    isGross: config.priceType === 'gross',
    currency: config.currency,
    costVatId: vat.costVatId,
    priceVatId: vat.priceVatId,
    price: { ...moneyFields(money), units: 1 },
    groupPriceThresholds: [],
    pricingUnits: 1,
    lines: [
      {
        label,
        quantity,
        cost: money.cost,
        payable: money.payable,
        priceIncl: money.priceIncl,
      },
    ],
    appliedPromotions: listedPromotions(promotions, base),
    warnings: ctx.warnings,
  };
};

const groupThresholdMessage = (rooms: number, threshold: number): string =>
  `${rooms} rooms reach the group threshold (${threshold}): group terms apply — confirm with the supplier.`;

/**
 * Whether a product's static price carries any money: a positive cost or
 * price on its flat row, a room, a traveller row, a price period, or a group
 * threshold. A dynamic product without one prices its gaps (and undated
 * trips) from the fallback amount.
 */
export const hasStaticPrice = (
  price: unknown,
  groupPriceThresholds?: unknown
): boolean => {
  const hasMoney = (value: unknown): boolean => {
    if (Array.isArray(value)) return value.some(hasMoney);
    if (value === null || typeof value !== 'object') return false;
    const record = value as Record<string, unknown>;
    for (const key of ['cost', 'price', 'additionalPrice']) {
      const raw = Number(record[key]);
      if (Number.isFinite(raw) && raw > 0) return true;
    }
    return ['rooms', 'travellers', 'pricePeriods', 'prices'].some((key) =>
      hasMoney(record[key])
    );
  };
  return hasMoney(price) || hasMoney(groupPriceThresholds ?? []);
};

/** Rooms booked on a hotel price: each room's quantity (at least 1). */
export const roomsBooked = (price: unknown): number => {
  const rooms = (price as { rooms?: unknown } | null)?.rooms;
  if (!Array.isArray(rooms)) return 0;
  return rooms.reduce((sum: number, room) => {
    const units = (room as { units?: unknown } | null)?.units;
    return sum + (typeof units === 'number' && units > 0 ? units : 1);
  }, 0);
};

/** The group-threshold warning for a hotel item's current rooms, or null. */
export const hotelGroupThresholdWarning = (
  config: DynamicPricing,
  price: unknown
): DynamicPricingWarning | null => {
  if (config.pricingOption !== 'Night' || !config.groupThreshold) return null;
  const rooms = roomsBooked(price);
  return rooms >= config.groupThreshold
    ? {
        code: 'GROUP_THRESHOLD',
        message: groupThresholdMessage(rooms, config.groupThreshold),
      }
    : null;
};

/**
 * Price a dynamic product for one booking. Never throws on missing data —
 * gaps price at 0 and come back as `warnings` for the consultant.
 */
export const priceDynamicProduct = (
  config: DynamicPricing,
  params: DynamicPricingParams
): DynamicPriceResult => {
  const ctx: Ctx = { config, params, warnings: [] };
  switch (config.pricingOption) {
    case 'Night':
      return priceNights(ctx);
    case 'Pax':
      return pricePax(ctx);
    default:
      return priceBooking(ctx);
  }
};

/**
 * The money a priced item carries, as a comparable string. A later write to
 * the item whose fingerprint differs from the one the function stored is a
 * consultant's own price edit. Ids, labels and derived fields (net cost,
 * markup) are ignored; money is compared to the cent, VAT rates by id. The
 * rooms a hotel books (a room's `units`) are not money but booking: a change
 * re-prices the item instead (`roomUnits` keeps them, the format fingerprints
 * had before).
 */
export const dynamicPriceFingerprint = (
  item: {
    price: unknown;
    pricingType: string | null;
    isGross: boolean | null;
    currency: string | null;
    groupPriceThresholds?: unknown;
    costVatId: string | null;
    priceVatId: string | null;
  },
  options: { roomUnits?: boolean } = {}
): string => {
  const values: (string | number)[] = [];
  const walk = (value: unknown, inRoom = false): void => {
    if (Array.isArray(value)) {
      for (const entry of value) walk(entry, inRoom);
      return;
    }
    if (value === null || typeof value !== 'object') return;
    const record = value as Record<string, unknown>;
    // The pricing table saves a placeholder row for every traveller type of
    // the itinerary; an all-zero row carries no money to compare.
    const isEmptyRow =
      !('rooms' in record) &&
      !('travellers' in record && Array.isArray(record.travellers)) &&
      !record.cost &&
      !record.price;
    if (isEmptyRow && 'travellerId' in record) return;
    for (const key of [
      'cost',
      'price',
      'commission',
      'units',
      'startNight',
      'nightCount',
      'minPeople',
      'maxPeople',
      'additionalPrice',
    ]) {
      if (key === 'units' && inRoom && !options.roomUnits) continue;
      const raw = record[key];
      if (typeof raw === 'number' && Number.isFinite(raw)) {
        values.push(`${key}:${cents(raw)}`);
      }
    }
    if (typeof record.travellerId === 'string') {
      values.push(`t:${record.travellerId}`);
    }
    for (const nested of ['rooms', 'travellers', 'pricePeriods']) {
      if (nested in record) walk(record[nested], inRoom || nested === 'rooms');
    }
  };
  walk(item.price);
  walk(item.groupPriceThresholds ?? []);
  return [
    item.pricingType ?? '',
    item.isGross ? 'g' : 'n',
    item.currency ?? '',
    `vat:${item.costVatId ?? ''}/${item.priceVatId ?? ''}`,
    ...values,
  ].join('|');
};
